import React, { useEffect } from 'react';

import { Box, Button, Stack } from '@sunwisesoftware/sunwise-ui';
import { get, isEmpty, isNull } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { CardContainer, ShowErrors } from 'common/components';
import {
    ReactHookFormInput,
    ReactHookFormFile,
} from 'common/components/form/RHF';
import { PlaceholderProductDatasheet } from 'common/components/placeholders';

import * as actions from './actions';
import ProductDatasheet from './components/ProductDatasheet';
import { ALLOWED_EXTENSIONS } from './constants';
import * as selectors from './selectors';

const Container = ({
    control,
    datasheet,
    handleClickDelete,
    handleCloseForm,
    isEnabledEdition,
    isFetchingDetail,
    isFetchingProductDetail,
    isVisibleForm,
    productId,
    reset,
    setValue,
    showForm,
    uploadDatasheetErrors,
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        return () => {
            reset();
        };
    }, []);

    const renderContent = () => {
        if (isVisibleForm)
            return (
                <Stack spacing={2}>
                    <ReactHookFormInput
                        control={control}
                        disabled={!isEnabledEdition}
                        label={t('Name')}
                        name="datasheet_name"
                        placeholder={t('Name')}
                    />

                    <ReactHookFormFile
                        accept={ALLOWED_EXTENSIONS}
                        control={control}
                        disabled={!isEnabledEdition}
                        fullWidth
                        label={t('Attached file')}
                        maxSize={20000000}
                        name="datasheet_archive"
                        setValue={setValue}
                    />

                    <Box
                        hidden={
                            isEmpty(datasheet) ||
                            isNull(get(datasheet, 'data_sheet', null))
                        }
                        textAlign="right"
                    >
                        <Button
                            color="secondary"
                            fullWidth={false}
                            onClick={handleCloseForm}
                        >
                            {t('Cancel')}
                        </Button>
                    </Box>
                </Stack>
            );
        return (
            <PlaceholderProductDatasheet
                ready={isFetchingDetail || isFetchingProductDetail}
            >
                <ProductDatasheet
                    datasheet={datasheet}
                    isEnabledEdition={isEnabledEdition}
                    prepareDelete={handleClickDelete}
                    productId={productId}
                    showForm={showForm}
                />
            </PlaceholderProductDatasheet>
        );
    };

    return (
        <CardContainer
            isCollapsable={true}
            title={t('Data sheet')}
            loading={isFetchingDetail || isFetchingProductDetail}
            withDivider
            isOpen={true}
        >
            <>
                {renderContent()}
                <ShowErrors
                    errors={uploadDatasheetErrors}
                    pt={2}
                    severity="error"
                    variant="filled"
                />
            </>
        </CardContainer>
    );
};

Container.propTypes = {
    control: PropTypes.object,
    datasheet: PropTypes.object,
    handleClickDelete: PropTypes.func,
    handleCloseForm: PropTypes.func,
    isEnabledEdition: PropTypes.bool,
    isFetchingDetail: PropTypes.bool,
    isFetchingProductDetail: PropTypes.bool,
    isVisibleForm: PropTypes.bool,
    productId: PropTypes.string,
    reset: PropTypes.func,
    setValue: PropTypes.func,
    showForm: PropTypes.func,
    uploadDatasheetErrors: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    datasheet: selectors.getDataProductDatasheet,
    uploadDatasheetErrors: selectors.getUploadDatasheetErrors,
    isFetchingProductDetail: selectors.getIsLoadingProductDatasheet,
    isUploadingDatasheet: selectors.getIsUploadingDatasheet,
    isVisibleForm: selectors.getIsVisibleForm,
});

const mapDispatchToProps = (dispatch) => ({
    handleClickDelete: (id) => dispatch(actions.prepareDelete(id)),
    handleCloseForm: () => dispatch(actions.prepareCloseForm()),
    reset: () => dispatch(actions.reset()),
    save: (values) => dispatch(actions.uploadFile(values)),
    showForm: (value) => dispatch(actions.showForm(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Container);
